import { useEffect, useRef, useState } from 'react'
import type { Column } from './DataTable'
import { COMPONENTS_COLUMN_MANAGER_TEXTS as TX } from '../../shared/texts'

// Popover next to the search button: toggle column visibility + reset widths and
// visibility to defaults. Sticky columns (e.g. row actions) stay always visible.

const ColumnsIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="18" rx="1" />
        <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
)

interface Props<T> {
    columns: Column<T>[]
    hidden: Set<string>
    onToggle: (key: string) => void
    onReset: () => void
}

export default function ColumnManager<T>({ columns, hidden, onToggle, onReset }: Props<T>) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
    }, [open])

    const items = columns.filter((c) => !c.sticky && typeof c.header === 'string' && c.header !== '')

    return (
        <div className="col-manager" ref={ref}>
            <button className={`icon-btn ${open ? 'active' : ''}`} title={TX.columns} aria-label={TX.columns} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                <ColumnsIcon />
            </button>
            {open && (
                <div className="col-manager-pop">
                    <div className="col-manager-head">{TX.columns}</div>
                    <div className="col-manager-list">
                        {items.map((c) => (
                            <label key={c.key} className="col-manager-item">
                                <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => onToggle(c.key)} />
                                <span>{c.header}</span>
                            </label>
                        ))}
                    </div>
                    <div className="col-manager-foot">
                        <button className="secondary" onClick={onReset}>{TX.reset}</button>
                    </div>
                </div>
            )}
        </div>
    )
}
